import { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import { showErrorAlert } from "@/Utils";
import {
  endImpersonationLocally,
  type ImpersonationContext,
} from "@/Utils/authSession";
import S from "./styles.module.scss";

interface ImpersonationBannerProps {
  context: ImpersonationContext;
}

const remainingLabel = (expiresAt: string) => {
  const remaining = Math.max(0, new Date(expiresAt).getTime() - Date.now());
  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const ImpersonationBanner = ({ context }: ImpersonationBannerProps) => {
  const { deleteRequest } = useApi();
  const [remaining, setRemaining] = useState(() =>
    remainingLabel(context.expiresAt)
  );
  const [ending, setEnding] = useState(false);

  const restoreOriginalSession = () => {
    const returnPath = endImpersonationLocally();
    window.location.assign(returnPath);
  };

  useEffect(() => {
    const update = () => {
      const label = remainingLabel(context.expiresAt);
      setRemaining(label);
      if (label === "00:00") restoreOriginalSession();
    };
    const interval = window.setInterval(update, 1000);
    return () => window.clearInterval(interval);
  }, [context.expiresAt]);

  const endAccess = async () => {
    setEnding(true);
    // Mantém o contexto local caso a API falhe, permitindo nova tentativa e
    // preservando a reconciliação da sessão/auditoria no servidor.
    try {
      await deleteRequest("/api/impersonation/current", undefined, {
        platform: true,
      });
      restoreOriginalSession();
    } catch (err: unknown) {
      showErrorAlert(
        err instanceof Error
          ? err.message
          : "Não foi possível encerrar o acesso assistido. Tente novamente."
      );
      setEnding(false);
    }
  };

  return (
    <aside className={S.banner} aria-label="Acesso assistido ativo">
      <div className={S.identity}>
        <strong>Acesso assistido</strong>
        <span>
          Você está operando como {context.targetUserName}. Ações serão
          atribuídas a {context.actorUserName}.
          {context.actorUserType === "support" && " Modo somente leitura."}
        </span>
      </div>
      <span className={S.timer} aria-label={`Tempo restante: ${remaining}`}>
        {remaining}
      </span>
      <button type="button" onClick={endAccess} disabled={ending}>
        {ending ? "Encerrando…" : "Encerrar acesso"}
      </button>
    </aside>
  );
};

export default ImpersonationBanner;
